import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { catchError, concatMap, map, of } from 'rxjs';
import { SmartintegService } from '../../../../services/smartinteg.service';
import { ToastService } from '../../../../services/toast.service';
import {
  Processo,
  TipoComando,
  TipoProvedor,
  Provedor,
  ProvedorConexao,
  ProvedorEndpoint,
  TipoRecurso,
  DirecaoPublicacao,
  FonteDadosPublicacao
} from '../../../../types/smartinteg';

@Component({
  selector: 'app-processo-wizard',
  templateUrl: './processo-wizard.component.html',
  styleUrls: ['./processo-wizard.component.css']
})
export class ProcessoWizardComponent implements OnInit {

  processoId!: string;
  processo: Processo | null = null;
  loading: boolean = true;
  loadingProvedores: boolean = false;

  steps: MenuItem[];
  activeIndex: number = 0;

  // Formulários por passo
  formImportacao!: FormGroup;
  formProcessamento!: FormGroup;
  formVisualizacao!: FormGroup;
  formExportacao!: FormGroup;
  formPublicacao!: FormGroup;

  // Listas para dropdowns
  provedores: Provedor[] = [];
  provedoresBD: Provedor[] = [];
  provedoresAPI: Provedor[] = [];

  tiposComando = [
    { label: 'SQL (Banco de Dados)', value: TipoComando.DATABASE_SQL },
    { label: 'Endpoint (API REST)', value: TipoComando.API_ENDPOINT }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private smartintegService: SmartintegService,
    private toastService: ToastService
  ) {
    this.steps = [
      { label: 'Importação', command: (event: any) => this.validarEAperceber(0) },
      { label: 'Processamento', command: (event: any) => this.validarEAperceber(1) },
      { label: 'Visualização', command: (event: any) => this.validarEAperceber(2) },
      { label: 'Exportação', command: (event: any) => this.validarEAperceber(3) },
      { label: 'Publicação', command: (event: any) => this.validarEAperceber(4) },
    ];
  }

  ngOnInit(): void {
    this.processoId = this.route.snapshot.paramMap.get('processoId')!;
    this.inicializarFormularios();
    if (this.processoId) {
      this.carregarProcesso();
      this.carregarProvedores();
    } else {
      this.toastService.erro('Erro', 'ID do processo não encontrado.');
      this.voltar();
    }
  }

  inicializarFormularios(): void {
    // Passo 1: Importação
    this.formImportacao = this.fb.group({
      nomeRecurso: ['', Validators.required],
      descricaoRecurso: [''],
      nomeComando: ['', Validators.required],
      tipoComando: [TipoComando.DATABASE_SQL, Validators.required],
      provedorId: ['', Validators.required],
      conexaoId: ['', Validators.required],
      comando: [''],
      endpointId: [''],
      camposChave: [[]]
    });

    // Observar mudanças no tipo de comando para ajustar validadores
    this.formImportacao.get('tipoComando')?.valueChanges.subscribe(tipo => {
      this.ajustarValidadoresImportacao(tipo);
    });

    // Passo 2: Processamento
    this.formProcessamento = this.fb.group({
      nomeRecurso: ['', Validators.required],
      descricaoRecurso: [''],
      nomeMapeamento: ['', Validators.required],
      mapeamentos: [{}]
    });

    // Passo 3: Visualização
    this.formVisualizacao = this.fb.group({
      nomeRecurso: ['', Validators.required],
      descricaoRecurso: [''],
      nomeMapeamento: ['', Validators.required],
      mapeamentos: [{}]
    });

    // Passo 4: Exportação
    this.formExportacao = this.fb.group({
      nomeRecurso: ['', Validators.required],
      descricaoRecurso: [''],
      nomeComando: ['', Validators.required],
      tipoComando: [TipoComando.API_ENDPOINT, Validators.required],
      provedorId: ['', Validators.required],
      conexaoId: ['', Validators.required],
      endpointId: ['']
    });

    // Passo 5: Publicação
    this.formPublicacao = this.fb.group({
      nomeRecurso: ['', Validators.required],
      codigo: ['', Validators.required],
      direcao: [DirecaoPublicacao.OUTBOUND, Validators.required]
    });
  }

  ajustarValidadoresImportacao(tipo: TipoComando): void {
    const comandoCtrl = this.formImportacao.get('comando');
    const endpointCtrl = this.formImportacao.get('endpointId');

    if (tipo === TipoComando.DATABASE_SQL) {
      comandoCtrl?.setValidators([Validators.required]);
      endpointCtrl?.clearValidators();
    } else {
      comandoCtrl?.clearValidators();
      endpointCtrl?.setValidators([Validators.required]);
    }

    comandoCtrl?.updateValueAndValidity();
    endpointCtrl?.updateValueAndValidity();
    
    // Resetar campos dependentes
    this.formImportacao.patchValue({
      provedorId: '',
      conexaoId: '',
      comando: '',
      endpointId: ''
    });
  }

  carregarProcesso(): void {
    this.loading = true;
    this.smartintegService.buscarProcesso(this.processoId).subscribe({
      next: (data) => {
        this.processo = data;
        this.loading = false;
        
        // Sugerir nomes padrão baseados no processo
        this.formImportacao.patchValue({
          nomeRecurso: `Importação - ${data.nome}`,
          nomeComando: `Comando Importação - ${data.nome}`
        });
      },
      error: (err) => {
        this.toastService.erro('Erro', 'Falha ao buscar dados do processo.');
        this.loading = false;
        this.voltar();
      }
    });
  }

  carregarProvedores(): void {
    this.loadingProvedores = true;
    this.smartintegService.listarProvedores().subscribe({
      next: (data) => {
        this.provedores = data;
        this.provedoresBD = data.filter(p => p.tipo === TipoProvedor.BASEDADOS);
        this.provedoresAPI = data.filter(p => p.tipo === TipoProvedor.APIREST);
        this.loadingProvedores = false;
      },
      error: (err) => {
        this.toastService.erro('Erro', 'Falha ao carregar provedores.');
        this.loadingProvedores = false;
      }
    });
  }

  getProvedoresFiltrados(): Provedor[] {
    const tipoComando = this.formImportacao.get('tipoComando')?.value;
    return tipoComando === TipoComando.DATABASE_SQL ? this.provedoresBD : this.provedoresAPI;
  }

  onConexaoSelecionada(conexao: ProvedorConexao): void {
    this.formImportacao.patchValue({ conexaoId: conexao.id });
  }

  onEndpointSelecionado(endpoint: ProvedorEndpoint): void {
    this.formImportacao.patchValue({ endpointId: endpoint.id });
  }

  onMapeamentosAlterados(mapeamentos: any, passo: number): void {
    if (passo === 1) {
      this.formProcessamento.patchValue({ mapeamentos });
    } else if (passo === 2) {
      this.formVisualizacao.patchValue({ mapeamentos });
    }
  }

  validarEAperceber(index: number): void {
    // Se tentar pular para frente, validar o passo atual
    if (index > this.activeIndex) {
      if (this.validarPassoAtual()) {
        this.activeIndex = index;
        this.prepararNovoPasso(index);
      }
    } else {
      this.activeIndex = index;
    }
  }

  prepararNovoPasso(index: number): void {
    if (index === 1 && !this.formProcessamento.get('nomeRecurso')?.value) {
      this.formProcessamento.patchValue({
        nomeRecurso: `Processamento - ${this.processo?.nome}`,
        nomeMapeamento: `Mapeamento - ${this.processo?.nome}`
      });
    } else if (index === 2 && !this.formVisualizacao.get('nomeRecurso')?.value) {
      this.formVisualizacao.patchValue({
        nomeRecurso: `Visualização - ${this.processo?.nome}`,
        nomeMapeamento: `Mapeamento Visual - ${this.processo?.nome}`
      });
    } else if (index === 3 && !this.formExportacao.get('nomeRecurso')?.value) {
      this.formExportacao.patchValue({
        nomeRecurso: `Exportação - ${this.processo?.nome}`,
        nomeComando: `Comando Exportação - ${this.processo?.nome}`
      });
    } else if (index === 4 && !this.formPublicacao.get('nomeRecurso')?.value) {
      this.formPublicacao.patchValue({
        nomeRecurso: `Publicação - ${this.processo?.nome}`,
        codigo: `${this.processo?.nome?.toLowerCase().replace(/\s+/g, '-')}-api`
      });
    }
  }

  validarPassoAtual(): boolean {
    let form: FormGroup;
    switch (this.activeIndex) {
      case 0: form = this.formImportacao; break;
      case 1: form = this.formProcessamento; break;
      case 2: form = this.formVisualizacao; break;
      case 3: form = this.formExportacao; break;
      case 4: form = this.formPublicacao; break;
      default: return true;
    }

    if (form.invalid) {
      this.toastService.alerta('Atenção', 'Preencha todos os campos obrigatórios do passo atual.');
      Object.keys(form.controls).forEach(key => form.get(key)?.markAsTouched());
      return false;
    }
    return true;
  }

  campoInvalido(campo: string, passo: number): boolean {
    let form: FormGroup;
    switch (passo) {
      case 0: form = this.formImportacao; break;
      case 1: form = this.formProcessamento; break;
      case 2: form = this.formVisualizacao; break;
      case 3: form = this.formExportacao; break;
      case 4: form = this.formPublicacao; break;
      default: return false;
    }
    const control = form.get(campo);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  nextStep(): void {
    if (this.validarPassoAtual()) {
      if (this.activeIndex < this.steps.length - 1) {
        this.activeIndex++;
        this.prepararNovoPasso(this.activeIndex);
      }
    }
  }

  prevStep(): void {
    if (this.activeIndex > 0) {
      this.activeIndex--;
    }
  }

  salvarWizard(): void {
    if (!this.validarPassoAtual()) return;

    this.loading = true;
    
    // 1. Salvar Importação
    const dadosImport = this.mapearDadosImportacao();
    
    this.smartintegService.criarRecursoImportacaoCompleto(dadosImport).pipe(
      // Precisamos do ID do comando criado para os mapeamentos
      concatMap(recurso => this.smartintegService.listarComandosPorRecurso(recurso.id).pipe(
        map(comandos => ({ recurso, comandoId: comandos[0].id }))
      )),
      // 2. Salvar Processamento
      concatMap(({ comandoId }) => {
        const dadosProc = this.mapearDadosProcessamento(comandoId);
        return this.smartintegService.criarRecursoProcessamentoCompleto(dadosProc).pipe(
          map(() => ({ comandoId }))
        );
      }),
      // 3. Salvar Visualização
      concatMap(({ comandoId }) => {
        const dadosVis = this.mapearDadosVisualizacao(comandoId);
        return this.smartintegService.criarRecursoVisualizacaoCompleto(dadosVis);
      }),
      // 4. Salvar Exportação
      concatMap(() => {
        const dadosExp = this.mapearDadosExportacao();
        return this.smartintegService.criarRecursoExportacaoCompleto(dadosExp);
      }),
      // 5. Salvar Publicação (opcional)
      concatMap(() => {
        if (this.formPublicacao.get('codigo')?.value) {
          const dadosPub = this.mapearDadosPublicacao();
          return this.smartintegService.criarRecursoPublicacaoCompleto(dadosPub);
        }
        return of(null);
      }),
      catchError(error => {
        this.toastService.erro('Erro', 'Ocorreu um erro ao salvar o fluxo: ' + (error.error?.message || error.message));
        this.loading = false;
        throw error;
      })
    ).subscribe({
      next: () => {
        this.toastService.sucesso('Sucesso', 'Fluxo completo configurado com sucesso!');
        this.loading = false;
        this.voltar();
      }
    });
  }

  private mapearDadosImportacao() {
    const val = this.formImportacao.value;
    return {
      processoId: this.processoId,
      nomeRecurso: val.nomeRecurso,
      descricaoRecurso: val.descricaoRecurso,
      ordemExecucao: 1,
      nomeComando: val.nomeComando,
      tipoComando: val.tipoComando,
      conexaoId: val.conexaoId,
      endpointId: val.endpointId,
      comando: val.comando,
      camposChave: val.camposChave,
      parametros: {}
    };
  }

  private mapearDadosProcessamento(comandoOrigemId: number) {
    const val = this.formProcessamento.value;
    return {
      processoId: this.processoId,
      nomeRecurso: val.nomeRecurso,
      descricaoRecurso: val.descricaoRecurso,
      ordemExecucao: 2,
      nomeMapeamento: val.nomeMapeamento,
      comandoOrigemId: comandoOrigemId,
      mapeamentos: val.mapeamentos,
      filtros: {}
    };
  }

  private mapearDadosVisualizacao(comandoOrigemId: number) {
    const val = this.formVisualizacao.value;
    return {
      processoId: this.processoId,
      nomeRecurso: val.nomeRecurso,
      descricaoRecurso: val.descricaoRecurso,
      ordemExecucao: 3,
      nomeMapeamento: val.nomeMapeamento,
      comandoOrigemId: comandoOrigemId,
      mapeamentos: val.mapeamentos
    };
  }

  private mapearDadosExportacao() {
    const val = this.formExportacao.value;
    return {
      processoId: this.processoId,
      nomeRecurso: val.nomeRecurso,
      descricaoRecurso: val.descricaoRecurso,
      ordemExecucao: 4,
      nomeComando: val.nomeComando,
      tipoComando: val.tipoComando,
      conexaoId: val.conexaoId,
      endpointId: val.endpointId
    };
  }

  private mapearDadosPublicacao() {
    const val = this.formPublicacao.value;
    return {
      processoId: this.processoId,
      nomeRecurso: val.nomeRecurso,
      codigo: val.codigo,
      ordemExecucao: 5,
      direcao: val.direcao,
      fonteDados: FonteDadosPublicacao.REGISTROS_TRANSFORMADOS,
      ativo: true
    };
  }

  voltar(): void {
    this.router.navigate(['/smartinteg/processos']);
  }
}
